import api from '../config/api';
import { isApiEnabled } from '../config/appConfig';
import { getApiData, parseApiError } from './apiUtils';

const SESSION_KEY = 'blf_session_user';
const LEGACY_SESSION_KEY = 'blf_last_user';
export const AUTH_CHANGE_EVENT = 'blf-auth-change';

const notifyAuthChange = () => {
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

export const saveUserSession = (user, rememberMe = false) => {
  if (!user) {
    return;
  }

  const payload = JSON.stringify(user);

  try {
    window.localStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(LEGACY_SESSION_KEY);

    if (rememberMe) {
      window.localStorage.setItem(SESSION_KEY, payload);
    } else {
      window.sessionStorage.setItem(SESSION_KEY, payload);
    }

    notifyAuthChange();
  } catch (error) {
    // Ignorar errores de almacenamiento del navegador.
  }
};

export const getUserSession = () => {
  try {
    const fromLocal = window.localStorage.getItem(SESSION_KEY);
    if (fromLocal) {
      return JSON.parse(fromLocal);
    }

    const fromSession = window.sessionStorage.getItem(SESSION_KEY);
    if (fromSession) {
      return JSON.parse(fromSession);
    }

    const legacy = window.localStorage.getItem(LEGACY_SESSION_KEY);
    if (legacy) {
      return JSON.parse(legacy);
    }
  } catch (error) {
    return null;
  }

  return null;
};

export const isValidSession = (user) => Boolean(user && user.id);

export const clearUserSession = () => {
  try {
    window.localStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(LEGACY_SESSION_KEY);
    notifyAuthChange();
  } catch (error) {
    // Ignorar errores de almacenamiento del navegador.
  }
};

export const getUserDisplayName = (user) => {
  if (!user) {
    return '';
  }

  const nombre = (user.nombre || '').trim();
  const apellido = (user.apellido || '').trim();

  if (nombre && apellido) {
    return `${nombre} ${apellido}`;
  }

  return nombre || apellido || user.email || '';
};

export const getUserInitials = (user) => {
  if (!user) {
    return '';
  }

  const nombre = (user.nombre || '').trim();
  const apellido = (user.apellido || '').trim();

  if (nombre && apellido) {
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
  }

  if (nombre) {
    const parts = nombre.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return nombre.slice(0, 2).toUpperCase();
  }

  if (apellido) {
    return apellido.slice(0, 2).toUpperCase();
  }

  if (user.email) {
    return user.email.slice(0, 2).toUpperCase();
  }

  return 'U';
};

export const getUserAvatarUrl = (user) => {
  if (!user || !user.avatar_url) {
    return null;
  }

  return user.avatar_url;
};

export const canChangeAvatar = (user) => {
  if (!user) {
    return false;
  }

  return user.tipo === 'administrativo' || user.tipo === 'especialista' || user.tipo === 'paciente';
};

export const isSuperAdmin = (user) => {
  if (!user || !Array.isArray(user.roles)) {
    return false;
  }

  return user.roles.some((role) => role.codigo === 'super_admin');
};

export const canManageInventory = (user) => {
  if (!user || !Array.isArray(user.roles)) {
    return false;
  }

  return user.roles.some(
    (role) => role.codigo === 'super_admin' || role.codigo === 'editor_contenido'
  );
};

export const isProfileComplete = (user) => {
  if (!isValidSession(user)) {
    return true;
  }

  if (isSuperAdmin(user)) {
    return true;
  }

  return user.perfil_completo !== false;
};

export const mergeSessionUser = (updates) => {
  const current = getUserSession();

  if (!current || !current.id) {
    return null;
  }

  const rememberMe = Boolean(window.localStorage.getItem(SESSION_KEY));
  const merged = { ...current, ...updates };
  saveUserSession(merged, rememberMe);

  return merged;
};

export const loginUser = ({ email, password }) => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  const normalizedPassword = password || '';

  if (!normalizedEmail) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  if (!normalizedPassword) {
    return Promise.reject(new Error('La contraseña es obligatoria.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('El inicio de sesión requiere conexión con el servidor.'));
  }

  return api
    .post('/auth_login.php', {
      email: normalizedEmail,
      password: normalizedPassword,
    })
    .then((response) => getApiData(response, 'No se pudo iniciar sesión.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};
